import React, { useState } from 'react';
import { ApolloProvider, gql, useMutation, useQuery } from '@apollo/client';
import client from '../lib/apolloClient';
import SponsoredOwnersPage from '../components/Succession/SponsoredOwners/SponsoredOwnersPage';

// Succeedo Phase 5: the advisor's Sponsored owners page.
const WORKSPACE = gql`query SponsorWorkspace { sponsor_workspace }`;
const RESULT = 'ok workspace errors';

const UPDATE_PROFILE = gql`
  mutation SponsorUpdateProfile($title: String, $phone: String, $states: [String!], $acceptsRoutedOwners: Boolean) {
    sponsor_update_profile(title: $title, phone: $phone, states: $states, accepts_routed_owners: $acceptsRoutedOwners) { ${RESULT} }
  }
`;
const CREATE_CAMPAIGN = gql`
  mutation SponsorCreateCampaignLink($campaign: String!) { sponsor_create_campaign_link(campaign: $campaign) { ${RESULT} } }
`;
const SAVE_CONTACT = gql`
  mutation SponsorSaveContact($name: String!, $firm: String, $partnerCategory: String!, $email: String, $phone: String, $note: String) {
    sponsor_save_contact(name: $name, firm: $firm, partner_category: $partnerCategory, email: $email, phone: $phone, note: $note) { ${RESULT} }
  }
`;
const DELETE_CONTACT = gql`mutation SponsorDeleteContact($id: ID!) { sponsor_delete_contact(id: $id) { ${RESULT} } }`;
const READ_BRIEF = gql`mutation SponsorReadBrief($id: ID!) { sponsor_read_brief(id: $id) { ok brief errors } }`;
const RESPOND = gql`mutation SponsorRespond($id: ID!, $claim: Boolean!) { sponsor_respond_to_owner(id: $id, claim: $claim) { ${RESULT} } }`;

function SponsoredOwnersWithData() {
  const { data, loading, refetch } = useQuery(WORKSPACE, { fetchPolicy: 'network-only' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const [createCampaign] = useMutation(CREATE_CAMPAIGN);
  const [saveContact] = useMutation(SAVE_CONTACT);
  const [deleteContact] = useMutation(DELETE_CONTACT);
  const [readBrief] = useMutation(READ_BRIEF);
  const [respond] = useMutation(RESPOND);

  const perform = async (mutate, field, variables) => {
    setBusy(true);
    setError('');
    try {
      const { data: result } = await mutate({ variables });
      const payload = result?.[field];
      if (payload?.errors?.length) throw new Error(payload.errors.join(', '));
      await refetch();
      return payload;
    } catch (e) {
      setError(e.message || 'That did not save. Please try again.');
      throw e;
    } finally {
      setBusy(false);
    }
  };
  const quietly = (promise) => promise.catch(() => null);

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  const workspace = data?.sponsor_workspace;
  if (!workspace) return <div className="p-8 text-gray-500">Sponsored owners isn't available for your account.</div>;

  return (
    <SponsoredOwnersPage
      workspace={workspace}
      busy={busy}
      error={error}
      onUpdateProfile={(vars) => quietly(perform(updateProfile, 'sponsor_update_profile', vars))}
      onCreateCampaign={(campaign) => quietly(perform(createCampaign, 'sponsor_create_campaign_link', { campaign }))}
      onSaveContact={(c) => quietly(perform(saveContact, 'sponsor_save_contact', {
        name: c.name, firm: c.firm, partnerCategory: c.partner_category, email: c.email, phone: c.phone, note: c.note,
      }))}
      onDeleteContact={(id) => quietly(perform(deleteContact, 'sponsor_delete_contact', { id }))}
      onRespond={(id, claim) => quietly(perform(respond, 'sponsor_respond_to_owner', { id, claim }))}
      onReadBrief={async (id) => {
        const payload = await perform(readBrief, 'sponsor_read_brief', { id });
        return payload?.brief;
      }}
    />
  );
}

const SuccessionSponsoredOwnersContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <SponsoredOwnersWithData />
  </ApolloProvider>
);

export default SuccessionSponsoredOwnersContainer;
